import * as Styled from './booking-meeting-room-form.styles';

import {useFormContext} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {format} from 'date-fns';

import {Loader, withForm} from '@innowise-group/ui-kit';

import {DayTimeline} from '../day-timeline';
import {BookMeetingInfo} from '../book-meeting-info';
import {BookMeetingDate} from '../book-meeting-date';
import {BookMeetingTime} from '../book-meeting-time';
import {MeetingRoomModalMsg} from '../meeting-room-modal-msg';

import {useMeetingRoomBooking} from './use-meeting-room-booking.hook';
import {isTimeOccupied} from '../../utilities';

export type BookingMeetingRoomFormProps = {
  meetingRoomId: string;
  infoModal: (text: JSX.Element, title: string, button: string) => void;
  floorAddress: string;
};

export enum BookingMeetingRoomFormFields {
  Date = 'date',
  StartTime = 'startTime',
  EndTime = 'endTime',
  Timeline = 'timeline',
}

export type BookMeetingRoomFormValues = {
  date: Date;
  startTime: Date;
  endTime: Date;
};

const BookingMeetingRoomForm = ({meetingRoomId, infoModal, floorAddress}: BookingMeetingRoomFormProps) => {
  const {t} = useTranslation();
  const {
    handleSubmit,
    formState: {isValid, isDirty},
    watch,
    setValue,
    resetField,
  } = useFormContext();

  const chosenDate = format(watch(BookingMeetingRoomFormFields.Date) || new Date(), 'yyyy-MM-dd');
  const {events, isLoading, createMeetingRoomEvent, getMeetingRoomEvents} = useMeetingRoomBooking(
    meetingRoomId,
    chosenDate,
    floorAddress,
  );

  const handleOnSubmit = (values: BookMeetingRoomFormValues) => {
    createMeetingRoomEvent({start: values.startTime, end: values.endTime}).subscribe((data) => {
      resetField(BookingMeetingRoomFormFields.StartTime);
      resetField(BookingMeetingRoomFormFields.EndTime);
      getMeetingRoomEvents();

      if (isTimeOccupied(data)) {
        return infoModal(
          <MeetingRoomModalMsg text={t('modals.bookMeetingRoom.occupiedTimeMsg')} />,
          t(`modals.attention`),
          t(`buttons.ok`),
        );
      }

      if (data?.id) {
        const infoText = t('modals.bookMeetingRoom.successBookingMsg', {
          date: format(values.date, 'dd-MM-yyyy'),
          start: format(values.startTime, 'HH:mm'),
          end: format(values.endTime, 'HH:mm'),
        });

        infoModal(<MeetingRoomModalMsg text={infoText} />, t(`modals.attention`), t(`buttons.ok`));
      }
    });
  };

  return (
    <Styled.FormContainer onSubmit={handleSubmit(handleOnSubmit)}>
      <Styled.FormContent>
        <Styled.FormInfoWrapper>
          <BookMeetingDate />
          <Styled.FormLayout layoutType="grid">
            <BookMeetingTime eventsData={events} name={BookingMeetingRoomFormFields.StartTime} />
            <BookMeetingTime eventsData={events} name={BookingMeetingRoomFormFields.EndTime} />
          </Styled.FormLayout>

          <BookMeetingInfo />
        </Styled.FormInfoWrapper>
        {isLoading ? (
          <Styled.LoaderContainer>
            <Loader />
          </Styled.LoaderContainer>
        ) : (
          <DayTimeline
            setValue={setValue}
            value={{
              start: watch(BookingMeetingRoomFormFields.StartTime),
              end: watch(BookingMeetingRoomFormFields.EndTime),
            }}
            selectedDate={watch(BookingMeetingRoomFormFields.Date)}
            events={events}
          />
        )}
      </Styled.FormContent>
      <Styled.FormBtn type="submit" disabled={!isValid || !isDirty || isLoading} withLoader={isLoading}>
        {t('buttons.confirm')}
      </Styled.FormBtn>
    </Styled.FormContainer>
  );
};

export default withForm(BookingMeetingRoomForm);
